import { createRoot, Root } from 'react-dom/client';

import { Hint } from '@components/ui/hint/hint';

const HINT_CLASS_NAME = 'colheader-hint-wrapper';

const rootRegistry = new WeakMap<HTMLSpanElement, Root>();

interface ApplyColHeaderHintParams {
  TH: HTMLTableCellElement;
  rowIndex?: number;
  title: string;
  tooltip: string;
}

// Mounts a <Hint> as the HoT column header's label. Known issues to revisit:
//   - antd tooltip caret position is off inside HoT's DOM
//   - flashes on cell edits when HoT re-fires afterGetColHeader
// Kept minimal until we tackle those follow-ups.
export const applyColHeaderHint = ({
  TH,
  rowIndex,
  title,
  tooltip,
}: ApplyColHeaderHintParams): void => {
  if (rowIndex !== 1) return;

  const colHeader = TH.querySelector('.colHeader');
  if (!colHeader) return;

  const existingWrapper = colHeader.querySelector<HTMLSpanElement>(`.${HINT_CLASS_NAME}`);
  if (existingWrapper) {
    rootRegistry.get(existingWrapper)?.render(<Hint title={tooltip}>{title}</Hint>);
    return;
  }

  Array.from(colHeader.childNodes).forEach(node => {
    if (node.nodeType === Node.TEXT_NODE) node.remove();
  });

  const wrapper = document.createElement('span');
  wrapper.className = HINT_CLASS_NAME;
  colHeader.appendChild(wrapper);
  const root = createRoot(wrapper);
  rootRegistry.set(wrapper, root);
  root.render(<Hint title={tooltip}>{title}</Hint>);
};

// Unmounts every hint root under `rootEl` and removes its wrapper. Called from
// LineItemsTable's unmount effect scoped to that instance's hotInstance.rootElement
// so multi-lot events do not tear down each other's hints.
export const cleanupColHeaderHints = (rootEl: HTMLElement): void => {
  rootEl.querySelectorAll<HTMLSpanElement>(`.${HINT_CLASS_NAME}`).forEach(wrapper => {
    const root = rootRegistry.get(wrapper);
    if (root) {
      root.unmount();
      rootRegistry.delete(wrapper);
    }
    wrapper.remove();
  });
};
